import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { lastValueFrom, Subject, timeout } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AiService, Conversation, ConversationTurnStatus } from './ai.service';
import { AuthService } from './auth.service';
import { AiToolEventsService } from './ai-tool-events.service';
import {
  mergeWorkItems,
  NovaActivityIdentity,
  NovaWorkItem,
  ownsNovaConversation,
  workItemFromUpdate,
  workItemFromTurn,
  novaTaskName,
} from '../ai-chatbot/nova-activity';

interface Watch {
  chat: Conversation;
  cursor: string;
  baselineCursor?: string;
  initialized: boolean;
  pending: number;
  nextCheck: number;
}
interface TaskWatch {
  conversationId: string;
  turnId: string;
  name?: string;
  notifyOnFinish: boolean;
  nextCheck: number;
  terminal: boolean;
  finishedAt?: number;
}

@Injectable({ providedIn: 'root' })
export class NovaActivityService {
  private readonly ai = inject(AiService);
  private readonly auth = inject(AuthService);
  private readonly events = inject(AiToolEventsService);
  private readonly destroyRef = inject(DestroyRef);
  readonly items = signal<NovaWorkItem[]>([]);
  readonly sessionActive = signal(false);
  readonly loading = signal(true);
  readonly unavailable = signal(false);
  readonly notice = signal<NovaWorkItem | null>(null);
  private readonly openConversationSubject = new Subject<Conversation>();
  readonly openConversation$ = this.openConversationSubject.asObservable();
  private readonly workRequestedSubject = new Subject<void>();
  readonly workRequested$ = this.workRequestedSubject.asObservable();
  private readonly scopeChangedSubject = new Subject<void>();
  readonly scopeChanged$ = this.scopeChangedSubject.asObservable();
  readonly active = computed(() =>
    this.items().filter((item) => ['waiting', 'working'].includes(item.status))
  );
  readonly completed = computed(() =>
    this.items().filter((item) => !['waiting', 'working'].includes(item.status))
  );
  readonly unreadCount = computed(
    () => this.completed().filter((item) => item.unread).length
  );
  private identity: NovaActivityIdentity | null = null;
  private generation = 0;
  private watches = new Map<string, Watch>();
  private tasks = new Map<string, TaskWatch>();
  private visibleConversations = new Set<() => string | null>();
  private savedCursors: Record<string, string> = {};
  private readIds = new Set<string>();
  private knownIds = new Set<string>();
  private unreadIds = new Set<string>();
  private followedReports = new Set<string>();
  private authenticatedUserId?: string | null;
  private nextDiscovery = 0;
  private timer?: ReturnType<typeof setTimeout>;
  private noticeTimer?: ReturnType<typeof setTimeout>;
  private running = false;
  private destroyed = false;

  constructor() {
    this.auth.afAuth.authState.pipe(takeUntilDestroyed()).subscribe((user) => {
      this.authenticatedUserId = user?.uid ?? null;
      this.wake();
    });
    this.auth.organizationChanged$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.wake());
    effect(() => {
      const event = this.events.latestReportCreation();
      untracked(() => {
        if (!event || !this.sameIdentity(event)) return;
        // Only the website completion policy currently supplies durable background updates.
        if (!event.report.path.startsWith('/dashboard/website-scan/')) return;
        this.followedReports.add(
          JSON.stringify([event.conversationId, event.report.path])
        );
        const watch = this.watches.get(event.conversationId);
        if (watch) watch.nextCheck = 0;
        const item: NovaWorkItem = {
          kind: 'report',
          id: `receipt:${event.creation.proposalId}`,
          conversationId: event.conversationId,
          name: event.report.name,
          status: event.creation.state === 'unknown' ? 'attention' : 'waiting',
          detail:
            event.creation.state === 'unknown'
              ? 'The scan hasn’t been confirmed. Open the chat to check.'
              : 'I’m keeping an eye on this scan. I’ll let you know when there’s an update.',
          updatedAt: event.emittedAt,
          unread: false,
          reportPath: event.report.path,
        };
        // A late receipt must not replace a completion already delivered by the server.
        if (
          !this.items().some(
            (existing) =>
              existing.reportPath === item.reportPath &&
              existing.status === 'ready'
          )
        ) {
          this.items.update((items) => mergeWorkItems(items, item));
        }
        this.nextDiscovery = 0;
        this.save();
        this.wake();
      });
    });
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('storage', this.onStorage);
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.generation++;
      clearTimeout(this.timer);
      clearTimeout(this.noticeTimer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('storage', this.onStorage);
    });
    this.wake();
  }

  private readonly onVisibility = () => {
    if (!document.hidden) this.wake();
  };
  private readonly onStorage = () => this.wake();

  private currentIdentity(): NovaActivityIdentity | null {
    const userId = this.auth.getCurrentUser()?.uid;
    const organizationId = this.auth.getCurrentOrganization();
    return userId && userId === this.authenticatedUserId && organizationId
      ? { userId, organizationId }
      : null;
  }
  private sameIdentity(identity: NovaActivityIdentity | null): boolean {
    const current = this.currentIdentity();
    return (
      !!identity &&
      !!current &&
      identity.userId === current.userId &&
      identity.organizationId === current.organizationId
    );
  }
  private valid(generation: number): boolean {
    return (
      !this.destroyed &&
      generation === this.generation &&
      this.sameIdentity(this.identity)
    );
  }
  private storageKey(): string {
    return `novaWork:v1:${this.identity?.organizationId}:${this.identity?.userId}`;
  }

  private syncIdentity(): void {
    const identity = this.currentIdentity();
    if (JSON.stringify(identity) === JSON.stringify(this.identity)) return;
    this.generation++;
    this.identity = identity;
    this.watches.clear();
    this.tasks.clear();
    this.sessionActive.set(!!identity);
    this.savedCursors = {};
    this.readIds.clear();
    this.knownIds.clear();
    this.unreadIds.clear();
    this.followedReports.clear();
    this.nextDiscovery = 0;
    this.items.set([]);
    this.notice.set(null);
    this.scopeChangedSubject.next();
    this.unavailable.set(false);
    this.loading.set(!!identity);
    clearTimeout(this.noticeTimer);
    if (!identity) return;
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey()) || '{}');
      if (Array.isArray(saved.tasks))
        for (const ref of saved.tasks.slice(-40)) {
          if (
            typeof ref?.conversationId === 'string' &&
            typeof ref.turnId === 'string' &&
            /^[a-zA-Z0-9_-]{1,200}$/.test(ref.conversationId) &&
            /^[a-zA-Z0-9_-]{1,100}$/.test(ref.turnId)
          ) {
            this.tasks.set(ref.turnId, {
              conversationId: ref.conversationId,
              turnId: ref.turnId,
              notifyOnFinish: ref.active === true,
              nextCheck: 0,
              terminal: false,
              name:
                typeof ref.name === 'string' && ref.name.length <= 100
                  ? ref.name
                  : undefined,
              finishedAt:
                Number.isFinite(ref.finishedAt) && ref.finishedAt > 0
                  ? ref.finishedAt
                  : undefined,
            });
          }
        }
      if (Array.isArray(saved.followed))
        this.followedReports = new Set(
          saved.followed
            .filter(
              (key: unknown) => typeof key === 'string' && key.length <= 800
            )
            .slice(-500)
        );
      if (Array.isArray(saved.read))
        this.readIds = new Set(
          saved.read.filter((id: unknown) => typeof id === 'string').slice(-500)
        );
      if (Array.isArray(saved.known))
        this.knownIds = new Set(
          saved.known
            .filter((id: unknown) => typeof id === 'string')
            .slice(-500)
        );
      if (Array.isArray(saved.unread))
        this.unreadIds = new Set(
          saved.unread
            .filter((id: unknown) => typeof id === 'string')
            .slice(-500)
        );
      if (saved.cursors && typeof saved.cursors === 'object') {
        for (const [id, cursor] of Object.entries(saved.cursors).slice(-500)) {
          if (typeof cursor === 'string' && /^\d{1,19}$/.test(cursor))
            this.savedCursors[id] = cursor;
        }
      }
    } catch {
      /* Monitoring also works without browser storage. */
    }
  }

  private save(): void {
    if (!this.sameIdentity(this.identity)) return;
    const tasks = [...this.tasks.values()];
    try {
      localStorage.setItem(
        this.storageKey(),
        JSON.stringify({
          cursors: Object.fromEntries(
            Object.entries(this.savedCursors).slice(-500)
          ),
          read: [...this.readIds].slice(-500),
          known: [...this.knownIds].slice(-500),
          unread: [...this.unreadIds].slice(-500),
          followed: [...this.followedReports].slice(-500),
          tasks: [
            ...tasks.filter((task) => !task.terminal),
            ...tasks.filter((task) => task.terminal).reverse(),
          ]
            .slice(0, 40)
            .map((task) => ({
              conversationId: task.conversationId,
              turnId: task.turnId,
              active: !task.terminal,
              name: task.name,
              finishedAt: task.finishedAt,
            })),
        })
      );
    } catch {
      /* Keep the live state when storage is full or disabled. */
    }
  }

  track(chat: Conversation | null | undefined): void {
    this.syncIdentity();
    if (!chat || !this.identity || !ownsNovaConversation(chat, this.identity))
      return;
    const current = this.watches.get(chat.id);
    if (current) current.chat = chat;
    else
      this.watches.set(chat.id, {
        chat,
        cursor: '0',
        baselineCursor: this.savedCursors[chat.id],
        initialized: false,
        pending: 0,
        nextCheck: 0,
      });
    this.wake();
  }

  refresh(): void {
    this.nextDiscovery = 0;
    for (const task of this.tasks.values())
      if (!task.terminal) task.nextCheck = 0;
    for (const watch of this.watches.values()) watch.nextCheck = 0;
    this.wake();
  }

  openWork(): void {
    this.workRequestedSubject.next();
  }

  registerConversationView(
    readVisibleConversation: () => string | null
  ): () => void {
    this.visibleConversations.add(readVisibleConversation);
    return () => this.visibleConversations.delete(readVisibleConversation);
  }

  observeTurn(
    chat: Conversation,
    turn: ConversationTurnStatus,
    request?: string
  ): void {
    this.syncIdentity();
    if (
      !this.identity ||
      !ownsNovaConversation(chat, this.identity) ||
      turn.conversationId !== chat.id
    )
      return;
    this.track(chat);
    let task = this.tasks.get(turn.turnId);
    if (!task) {
      task = {
        conversationId: chat.id,
        turnId: turn.turnId,
        name: novaTaskName(request, chat.name),
        notifyOnFinish: true,
        nextCheck: 0,
        terminal: false,
      };
      this.tasks.set(turn.turnId, task);
    }
    this.applyTaskTurn(task, turn);
    this.save();
  }

  updateTaskProgress(
    conversationId: string,
    turnId: string,
    label: string
  ): void {
    if (
      !this.sameIdentity(this.identity) ||
      !label.trim() ||
      label.length > 200
    )
      return;
    const task = this.tasks.get(turnId);
    if (!task || task.terminal || task.conversationId !== conversationId)
      return;
    task.nextCheck = Date.now() + 7000;
    this.items.update((items) =>
      items.map((item) =>
        item.id === `turn:${turnId}`
          ? {
              ...item,
              detail: label,
              steps:
                item.steps?.at(-1) === label
                  ? item.steps
                  : [...(item.steps || []).slice(-3), label],
            }
          : item
      )
    );
  }

  private applyTaskTurn(task: TaskWatch, turn: ConversationTurnStatus): void {
    const chat = this.watches.get(task.conversationId)?.chat;
    if (!chat || turn.conversationId !== chat.id || turn.turnId !== task.turnId)
      return;
    const previous = this.items().find(
      (item) => item.id === `turn:${turn.turnId}`
    );
    const item = workItemFromTurn(
      turn,
      task.name || chat.name || 'Your request',
      previous
    );
    const terminal = ['completed', 'failed', 'cancelled'].includes(turn.status);
    if (task.terminal) return;
    if (terminal) {
      task.finishedAt ??= task.notifyOnFinish
        ? Date.now()
        : Date.parse(turn.createdAt) || Date.now();
      item.updatedAt = task.finishedAt;
    }
    const foreground =
      !document.hidden &&
      [...this.visibleConversations].some((read) => read() === chat.id);
    const newlyFinished =
      terminal &&
      !task.terminal &&
      task.notifyOnFinish &&
      !this.knownIds.has(item.id);
    item.unread =
      this.unreadIds.has(item.id) ||
      (newlyFinished && !foreground && turn.status !== 'cancelled');
    this.items.update((items) => mergeWorkItems(items, item));
    if (item.unread) this.unreadIds.add(item.id);
    if (newlyFinished && item.unread) this.showNotice(item);
    if (terminal) {
      this.knownIds.add(item.id);
      task.notifyOnFinish = false;
    }
    task.terminal = terminal;
    task.nextCheck = Date.now() + 7000;
  }

  private async pollTask(task: TaskWatch, generation: number): Promise<void> {
    task.nextCheck = Date.now() + 15000;
    try {
      const response = await lastValueFrom(
        this.ai
          .getConversationTurn(task.conversationId, task.turnId)
          .pipe(timeout(15000))
      );
      if (!this.valid(generation) || this.tasks.get(task.turnId) !== task)
        return;
      this.applyTaskTurn(task, response.turn);
    } catch (error) {
      if (!this.valid(generation)) return;
      if (
        [401, 403, 404].includes(Number((error as { status?: number })?.status))
      ) {
        this.tasks.delete(task.turnId);
        this.items.update((items) =>
          items.filter((item) => item.id !== `turn:${task.turnId}`)
        );
        if (this.notice()?.id === `turn:${task.turnId}`) this.dismissNotice();
      } else this.unavailable.set(true);
    }
  }

  private wake(): void {
    if (this.destroyed) return;
    clearTimeout(this.timer);
    this.syncIdentity();
    if (!this.running) void this.tick();
  }

  private async tick(): Promise<void> {
    this.running = true;
    const generation = this.generation;
    try {
      if (!this.identity || document.hidden) return;
      if (Date.now() >= this.nextDiscovery) {
        this.nextDiscovery = Date.now() + 60000;
        try {
          const chats = await lastValueFrom(
            this.ai.getChatbotConversations().pipe(timeout(15000))
          );
          if (!this.valid(generation)) return;
          const owned = chats
            .filter((chat) => ownsNovaConversation(chat, this.identity!))
            .sort(
              (a, b) =>
                Date.parse(b.updatedAt || b.createdAt) -
                Date.parse(a.updatedAt || a.createdAt)
            );
          const ids = new Set(owned.map((chat) => chat.id));
          for (const [id] of this.watches)
            if (!ids.has(id)) this.watches.delete(id);
          for (const [id, task] of this.tasks)
            if (!ids.has(task.conversationId)) this.tasks.delete(id);
          this.items.update((items) =>
            items.filter((item) => ids.has(item.conversationId))
          );
          for (const chat of owned) {
            const existing = this.watches.get(chat.id);
            if (existing) existing.chat = chat;
            else
              this.watches.set(chat.id, {
                chat,
                cursor: '0',
                baselineCursor: this.savedCursors[chat.id],
                initialized: false,
                pending: 0,
                nextCheck: 0,
              });
          }
          this.unavailable.set(false);
        } catch {
          if (this.valid(generation)) this.unavailable.set(true);
        }
      }
      if (!this.valid(generation)) return;
      await Promise.allSettled(
        [...this.tasks.values()]
          .filter(
            (task) =>
              !task.terminal &&
              task.nextCheck <= Date.now() &&
              this.watches.has(task.conversationId)
          )
          .slice(0, 4)
          .map((task) => this.pollTask(task, generation))
      );
      if (!this.valid(generation)) return;
      // Limit each pass and prioritize active work; idle histories rotate without request bursts.
      const activeChats = new Set(
        this.active().map((item) => item.conversationId)
      );
      const due = [...this.watches.values()]
        .filter((watch) => watch.nextCheck <= Date.now())
        .sort(
          (a, b) =>
            Number(b.pending > 0 || activeChats.has(b.chat.id)) -
              Number(a.pending > 0 || activeChats.has(a.chat.id)) ||
            a.nextCheck - b.nextCheck
        )
        .slice(0, 4);
      await Promise.allSettled(
        due.map((watch) => this.poll(watch, generation))
      );
      if (this.valid(generation)) {
        this.loading.set(false);
        this.save();
      }
    } finally {
      this.running = false;
      if (!this.destroyed)
        this.timer = setTimeout(() => {
          this.syncIdentity();
          void this.tick();
        }, 5000);
    }
  }

  private async poll(watch: Watch, generation: number): Promise<void> {
    watch.nextCheck = Date.now() + 60000;
    try {
      const page = await lastValueFrom(
        this.ai
          .getConversationUpdates(watch.chat.id, watch.cursor)
          .pipe(timeout(15000))
      );
      if (!this.valid(generation) || this.watches.get(watch.chat.id) !== watch)
        return;
      const previousCursor = watch.baselineCursor;
      // Rebuild recent activity on reload, but never replay old history as fresh popups.
      const freshPage =
        watch.initialized ||
        (previousCursor !== undefined &&
          BigInt(page.nextCursor) > BigInt(previousCursor));
      for (const update of page.updates) {
        const existing = this.items().find((item) => item.id === update.id);
        const result = workItemFromUpdate(watch.chat, update, false);
        const followedReport =
          !!result.reportPath &&
          this.followedReports.has(
            JSON.stringify([watch.chat.id, result.reportPath])
          );
        const unread =
          existing?.unread ??
          (this.unreadIds.has(update.id) ||
            ((freshPage || followedReport) &&
              !this.knownIds.has(update.id) &&
              !this.readIds.has(update.id)));
        const item = { ...result, unread };
        this.items.update((items) => mergeWorkItems(items, item));
        if (unread && !existing && !this.knownIds.has(update.id))
          this.showNotice(item);
        if (unread) this.unreadIds.add(update.id);
        this.knownIds.add(update.id);
      }
      const stoppedWithoutUpdate =
        watch.pending > 0 &&
        !page.pendingTasks &&
        !page.hasMore &&
        !page.updates.length;
      const previousWork = this.active().find(
        (item) => item.kind !== 'task' && item.conversationId === watch.chat.id
      );
      watch.cursor = page.nextCursor;
      watch.pending = page.pendingTasks;
      if (!page.hasMore) watch.initialized = true;
      if (
        !this.savedCursors[watch.chat.id] ||
        BigInt(page.nextCursor) >= BigInt(this.savedCursors[watch.chat.id])
      ) {
        this.savedCursors[watch.chat.id] = page.nextCursor;
      }
      const pendingId = `pending:${watch.chat.id}`;
      this.items.update((items) =>
        items.filter((item) => item.id !== pendingId)
      );
      if (stoppedWithoutUpdate) {
        const item: NovaWorkItem = {
          kind: 'report',
          id: `status:${watch.chat.id}:${page.nextCursor}`,
          conversationId: watch.chat.id,
          name: previousWork?.name || watch.chat.name || 'Report update',
          status: 'attention',
          unread: true,
          updatedAt: Date.now(),
          reportPath: previousWork?.reportPath,
          detail:
            'This background task is no longer active. Let’s check the outcome in your chat.',
        };
        this.items.update((items) => mergeWorkItems(items, item));
        this.unreadIds.add(item.id);
        this.showNotice(item);
      }
      if (
        page.pendingTasks > 0 &&
        !this.items().some(
          (item) =>
            item.conversationId === watch.chat.id &&
            item.kind !== 'task' &&
            ['waiting', 'working'].includes(item.status)
        )
      ) {
        this.items.update((items) =>
          mergeWorkItems(items, {
            id: pendingId,
            kind: 'report',
            conversationId: watch.chat.id,
            name: watch.chat.name || 'Website report',
            status: 'working',
            unread: false,
            updatedAt: Date.now(),
            detail: `I’m following ${
              page.pendingTasks === 1
                ? 'this report'
                : `${page.pendingTasks} reports`
            }. You can keep working.`,
          })
        );
      }
      const recentReceipt = this.items().some(
        (item) =>
          item.conversationId === watch.chat.id &&
          item.id.startsWith('receipt:') &&
          ['waiting', 'working'].includes(item.status) &&
          Date.now() - item.updatedAt < 90000
      );
      if (!page.pendingTasks && !page.hasMore && !recentReceipt) {
        this.items.update((items) =>
          items.map((item) =>
            item.conversationId === watch.chat.id &&
            item.kind !== 'task' &&
            ['waiting', 'working'].includes(item.status)
              ? {
                  ...item,
                  status: 'attention',
                  detail:
                    'No background task is active. Open the chat to check the latest report status.',
                }
              : item
          )
        );
      }
      watch.nextCheck =
        Date.now() +
        (page.hasMore
          ? 1000
          : page.pendingTasks || recentReceipt || stoppedWithoutUpdate
          ? 5000
          : 60000);
    } catch (error) {
      if (!this.valid(generation)) return;
      const status = Number((error as { status?: number })?.status);
      if ([401, 403, 404].includes(status)) {
        this.watches.delete(watch.chat.id);
        this.items.update((items) =>
          items.filter((item) => item.conversationId !== watch.chat.id)
        );
        if (this.notice()?.conversationId === watch.chat.id)
          this.dismissNotice();
      } else {
        this.unavailable.set(true);
        watch.nextCheck = Date.now() + 15000;
      }
    }
  }

  private showNotice(item: NovaWorkItem): void {
    this.notice.set(item);
    clearTimeout(this.noticeTimer);
    this.noticeTimer = setTimeout(() => this.notice.set(null), 12000);
  }
  dismissNotice(): void {
    this.notice.set(null);
    clearTimeout(this.noticeTimer);
  }
  markRead(item: NovaWorkItem): void {
    this.readIds.add(item.id);
    this.unreadIds.delete(item.id);
    this.items.update((items) =>
      items.map((current) =>
        current.id === item.id ? { ...current, unread: false } : current
      )
    );
    if (this.notice()?.id === item.id) this.dismissNotice();
    this.save();
  }
  openItem(item: NovaWorkItem): void {
    this.syncIdentity();
    const watch = this.watches.get(item.conversationId);
    if (!this.items().some((current) => current.id === item.id) || !watch)
      return;
    this.openConversationSubject.next(watch.chat);
    this.markRead(item);
  }
}
